export type PlanId = "standard" | "pro" | "unlimited";

export type Question = {
  id: string;
  eyebrow: string;
  title: string;
  subtitle: string;
  options: Array<{
    id: string;
    label: string;
    description: string;
    icon: "image" | "folder" | "text" | "mail" | "search" | "layers" | "sparkles" | "shield" | "zap" | "clock" | "desktop" | "briefcase" | "users";
    tone: "blue" | "purple" | "teal" | "yellow" | "indigo";
  }>;
};

export const questions: Question[] = [
  {
    id: "copy_types",
    eyebrow: "Step 1 of 5",
    title: "What do you lose track of most often?",
    subtitle: "Pick what slips through your clipboard the most.",
    options: [
      {
        id: "screenshots",
        label: "Screenshots",
        description: "Quick captures, design passes, and saved moments.",
        icon: "image",
        tone: "purple"
      },
      {
        id: "images_and_text",
        label: "Copied text/images",
        description: "Links, snippets, and visuals you need again.",
        icon: "image",
        tone: "blue"
      },
      {
        id: "files",
        label: "Files and folders",
        description: "Assets, downloads, client docs, project folders.",
        icon: "folder",
        tone: "teal"
      },
      {
        id: "email",
        label: "Emails and Contact Details",
        description: "The details you need again five minutes later.",
        icon: "mail",
        tone: "indigo"
      }
    ]
  },
  {
    id: "workflow_drag",
    eyebrow: "Step 2 of 5",
    title: "Where does copy and paste slow you down?",
    subtitle: "Tell us the moment that breaks your flow.",
    options: [
      {
        id: "reopening",
        label: "Re-finding old copies",
        description: "You know you copied it, but not where it went.",
        icon: "search",
        tone: "blue"
      },
      {
        id: "handoff",
        label: "Moving between apps",
        description: "Screenshots, files, and context keep getting separated.",
        icon: "layers",
        tone: "teal"
      },
      {
        id: "cleanup",
        label: "Too much clutter",
        description: "History is useful until it becomes a junk drawer.",
        icon: "sparkles",
        tone: "yellow"
      }
    ]
  },
  {
    id: "recall_speed",
    eyebrow: "Step 3 of 5",
    title: "How often do you need something you copied earlier?",
    subtitle: "This sets how much time Linkhiv saves you.",
    options: [
      {
        id: "daily",
        label: "Many times a day",
        description: "I constantly move between apps and projects.",
        icon: "zap",
        tone: "purple"
      },
      {
        id: "few_times",
        label: "A few times a week",
        description: "Usually when I am deep in work and do not want to break focus.",
        icon: "clock",
        tone: "blue"
      },
      {
        id: "only_when_lost",
        label: "Only when I lose something",
        description: "But when I do, I really need it back quickly.",
        icon: "search",
        tone: "teal"
      }
    ]
  },
  {
    id: "privacy",
    eyebrow: "Step 4 of 5",
    title: "How important is local-only privacy?",
    subtitle: "Useful clipboard — no cloud creep.",
    options: [
      {
        id: "must_be_local",
        label: "Non-negotiable",
        description: "No account, no cloud sync, no remote clipboard storage.",
        icon: "shield",
        tone: "purple"
      },
      {
        id: "prefer_local",
        label: "Strong preference",
        description: "I want useful history without sending my work elsewhere.",
        icon: "desktop",
        tone: "blue"
      },
      {
        id: "mostly_speed",
        label: "Speed matters most",
        description: "Privacy is good, but instant recall is the main win.",
        icon: "zap",
        tone: "teal"
      }
    ]
  },
  {
    id: "usage",
    eyebrow: "Step 5 of 5",
    title: "Which setup sounds closest?",
    subtitle: "Last one — we'll match you to the right license.",
    options: [
      {
        id: "one_mac",
        label: "One main Mac",
        description: "Personal workflow, simple setup, one device.",
        icon: "desktop",
        tone: "blue"
      },
      {
        id: "multi_mac",
        label: "Up to five Macs",
        description: "Work machine, home machine, or a multi-device setup.",
        icon: "briefcase",
        tone: "purple"
      },
      {
        id: "team_research",
        label: "Testing for a team",
        description: "Start with the best value license and expand later.",
        icon: "users",
        tone: "indigo"
      }
    ]
  }
];

export const plans = [
  {
    id: "standard" as const,
    name: "Standard",
    originalPrice: "$49",
    price: "$29",
    detail: "1 device",
    description: "Best for a personal Mac workflow.",
    cta: "Get Standard",
    features: [
      "One-time purchase",
      "Use on 1 device",
      "Full feature set",
      "Lifetime updates"
    ]
  },
  {
    id: "pro" as const,
    name: "Pro",
    originalPrice: "$69",
    price: "$39",
    detail: "5 devices",
    description: "Best value for professional use across your Macs.",
    cta: "Get Pro",
    badge: "Popular",
    badgeVariant: "primary" as const,
    features: [
      "One-time purchase",
      "Use on up to 5 devices",
      "Full feature set",
      "Lifetime updates",
      "Email support"
    ]
  }
];

export const checkoutPlans = plans;

export const valueChips = [
  "Private by design",
  "Native to macOS",
  "One-time payment",
  "Screenshots included"
] as const;

export const feedbackByQuestionId: Record<string, { title: string; description: string; bullets: string[] }> = {
  workflow_drag: {
    title: "That's exactly where Linkhiv helps.",
    description: "One timeline keeps every clip in reach — no app hopping.",
    bullets: ["Hover preview before paste", "Files and screenshots grouped", "No cloud workspace"]
  }
};

const reviewBanking = {
  quote:
    "I kept losing copied client details, invoice numbers, and links during the day. Now I press Shift Command V and the thing I copied earlier is just there.",
  author: "Priya",
  role: "Operations assistant",
  image: "/assets/reviews/dillon-kydd-headshot.jpg"
} as const;

const reviewAccountant = {
  quote:
    "The main reason I trusted Linkhiv is that my clipboard history stays on my Mac. I copy private work details all day, so local first mattered.",
  author: "Sam",
  role: "Accountant",
  image: "/assets/reviews/man-1.webp"
} as const;

const reviewResearcher = {
  quote:
    "I bought it for clipboard history, but screenshot history is what made me keep using it. It keeps visual references available without clutter.",
  author: "Nina",
  role: "UX researcher",
  image: "/assets/reviews/girl-1.webp"
} as const;

const reviewCafe = {
  quote:
    "I did not want another subscription. Paying once for a Mac utility I use every day made the decision easy.",
  author: "Marco",
  role: "Cafe owner",
  image: "/assets/reviews/darshan-patel-headshot-96w.webp"
} as const;

/** Four checkout-objection reviews; one card each with distinct headshots. */
export const testimonials = [
  reviewBanking,
  reviewAccountant,
  reviewResearcher,
  reviewCafe
] as const;

export type PaymentExplainerStep = {
  title: string;
  description: string;
  icon: Question["options"][number]["icon"];
  tone: Question["options"][number]["tone"];
  bonus?: boolean;
};

export const paymentExplainerSteps: PaymentExplainerStep[] = [
  {
    title: "Copy or screenshot like you normally do",
    description: "Every time you copy text, files, links, or grab a screenshot, Linkhiv quietly adds it to your history.",
    icon: "layers",
    tone: "teal"
  },
  {
    title: "Press one shortcut anywhere on your Mac",
    description: "Open Linkhiv instantly from whatever app you are in instead of reopening tabs, folders, notes, or old messages.",
    icon: "desktop",
    tone: "blue"
  },
  {
    title: "Preview the right item, then paste",
    description: "Use quick previews and search to spot the exact thing you need and drop it back in within seconds.",
    icon: "search",
    tone: "purple"
  },
  {
    title: "Categorizes things automatically",
    description: "Copied text, screenshots, files, folders, and email are grouped so history is easier to scan.",
    icon: "layers",
    tone: "blue"
  },
  {
    title: "Screenshots are added automatically",
    description:
      "Linkhiv also picks up screenshots in the background, so visual references are there when you need them without making the app feel busy.",
    icon: "image",
    tone: "yellow",
    bonus: true
  }
];

export const paymentFaqs = [
  {
    question: "Is this a subscription?",
    answer: "No. Linkhiv is a one-time purchase with lifetime updates."
  },
  {
    question: "What happens after I pay?",
    answer:
      "Stripe confirms your payment, then your download link and license key are sent to the email used at checkout."
  },
  {
    question: "Which plan should I choose?",
    answer: "Standard covers one Mac. Pro covers up to five personal Macs and includes email support."
  },
  {
    question: "What if it is not right for me?",
    answer:
      "Try Linkhiv in your real workflow. If it does not feel like a clear upgrade, contact us within 30 days and we will take care of you with a full refund."
  },
  {
    question: "Does Linkhiv upload my clipboard history?",
    answer:
      "No. Your copied text, screenshots, files, and links stay on your Mac. Linkhiv does not upload them to a cloud workspace."
  },
  {
    question: "Is checkout secure?",
    answer: "Yes. Payment is handled by Stripe, and Linkhiv does not store your card details."
  }
] as const;
